<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    @reset="resetForm"
    @close="closeForm(true)"
    @submit.prevent="onSubmit"
  >
    <div class="row q-col-gutter-xs">
      <div class="col-12">
        <PInput
          v-model.trim="formData.name"
          label="Role Name"
          placeholder="Enter role name"
          required
          debounce="300"
          :disable="formState.view"
          :rules="[validateRole]"
        ></PInput>
      </div>
      <div class="col-12">
        <PInput
          v-model.trim="formData.description"
          label="Description"
          placeholder="Enter description for this role"
          :disable="formState.view"
          required
          type="textarea"
          autogrow
        ></PInput>
      </div>
      <div class="col-12 q-pb-xxs">
        <PField
          v-model="formData.applicable_to"
          label="Applicable To"
          :rules="[validateApplicableTo]"
          required
        >
          <template #control>
            <POptionGroup
              v-model="formData.applicable_to"
              :options="applicableOptions"
              required
              :disable="formState.view"
            />
          </template>
        </PField>
      </div>
      <div class="col-12">
        <span class="block text-lg q-pb-sm">
          Permissions
          <span class="text-error-60"> * </span>
        </span>
        <q-separator class="q-mb-xxs" />
      </div>
      <div class="col-12">
        <div
          v-if="formState.view"
          class="row item-center bg-info-10 text-info-60 q-pa-xxs"
        >
          <InfoIcon class="icon q-mr-xs" />
          <span class="col-10">
            To customize permissions for the specific user, go to roles
            management.
          </span>
        </div>
        <ModulePermissionsList
          v-model="formData.permissions"
          :highlight-changes="false"
          :show-select-all-reset="formState.view ? false : true"
          bordered
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar } from "quasar";
import roleFormModel from "src/modules/roles-management/models/roleForm.js";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import InfoIcon from "assets/icons/info.svg";
const $q = useQuasar();
const pForm = ref(null);

const { formState, role, initialPermissions } = storeToRefs(
  useRoleManagementStore(),
);
const { toggleRoleForm, postRole, putRole, getRoleValidations } =
  useRoleManagementStore();
const { formData, isLoading, resetForm, closeForm, submitForm } = useForm({
  formModel: roleFormModel({
    permissions: structuredClone(toRaw(unref(initialPermissions))),
  }),
  formRef: pForm,
  isForm: formState,
  entity: role,
  post: postRole,
  put: putRole,
  toggleForm: toggleRoleForm,
});

const applicableOptions = [
  { label: "Internal Stakeholders", value: "internal" },
  { label: "External Stakeholders", value: "external" },
];

const formHeading = computed(() => {
  if (formState.value.view) {
    return "Preview Role";
  } else if (formState.value.edit) {
    return "Edit Role";
  } else {
    return "Add Role";
  }
});

const submitButton = computed(() => {
  if (unref(formState).view) {
    return {};
  } else {
    return {
      label: formState.value.edit ? "Update" : "Submit",
      loading: isLoading.value,
      disabled: !isFormDataChanged.value || isUsedRole.value,
    };
  }
});

const secondaryCta = computed(() => {
  if (unref(formState).view) {
    return {};
  }

  return { visible: true };
});

const isFormDataChanged = computed(() => {
  return (
    JSON.stringify(role.value) !==
    JSON.stringify({
      ...roleFormModel(),
    })
  );
});

const onSubmit = () => {
  if (formState.value.edit) {
    confirmRoleUpdate();
  } else {
    submitForm();
  }
};

const confirmRoleUpdate = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: "Update Role",
      text: "Are you sure you want to update this role?",
      labelClass: "text-neutral-40",
      label:
        "Please note the changes you make here will impact the overall role permissions of existing users",
      isFooterVisible: true,
      submitLabel: "Yes",
      submittable: true,
      cancelLabel: "Cancel",
      cancellable: true,
      closable: true,
      onConfirm: () => {
        submitForm();
      },
    },
  });
};

const isUsedRole = ref(false);
const errorMessage = ref("");

const validateRole = async (value) => {
  if (
    unref(formState).edit &&
    unref(role).name !== value &&
    errorMessage.value
  ) {
    isUsedRole.value = true;

    return errorMessage.value;
  } else {
    isUsedRole.value = false;
  }
};

const validateApplicableTo = async (value) => {
  if (
    unref(formState).edit &&
    unref(role).applicable_to?.[0] !== value[0] &&
    errorMessage.value
  ) {
    isUsedRole.value = true;

    return errorMessage.value;
  } else {
    isUsedRole.value = false;
  }
};

watch(
  () => unref(formState).edit,
  async (val) => {
    if (val) {
      try {
        await getRoleValidations(unref(role)?._id);

        errorMessage.value = null;
      } catch (err) {
        errorMessage.value = err?.response?.data?.errors?.[0]?.msg || null;
      }
    } else {
      errorMessage.value = null;
    }
  },
  {
    immediate: true,
  },
);
</script>
